import { h, proxyCustomElement, HTMLElement, createEvent, Host } from '@stencil/core/internal/client';
import { c as truncate } from './utils.js';
import { d as defineCustomElement$j } from './wpp-action-button2.js';
import { d as defineCustomElement$i } from './wpp-icon-cross2.js';
import { d as defineCustomElement$h } from './wpp-icon-database2.js';
import { d as defineCustomElement$g } from './wpp-icon-document2.js';
import { d as defineCustomElement$f } from './wpp-icon-error2.js';
import { d as defineCustomElement$e } from './wpp-icon-file2.js';
import { d as defineCustomElement$d } from './wpp-icon-file-zip2.js';
import { d as defineCustomElement$c } from './wpp-icon-image2.js';
import { d as defineCustomElement$b } from './wpp-icon-info2.js';
import { d as defineCustomElement$a } from './wpp-icon-music2.js';
import { d as defineCustomElement$9 } from './wpp-icon-pitch2.js';
import { d as defineCustomElement$8 } from './wpp-icon-spreadsheet2.js';
import { d as defineCustomElement$7 } from './wpp-icon-success2.js';
import { d as defineCustomElement$6 } from './wpp-icon-video-clip2.js';
import { d as defineCustomElement$5 } from './wpp-icon-warning2.js';
import { d as defineCustomElement$4 } from './wpp-inline-message2.js';
import { d as defineCustomElement$3 } from './wpp-internal-tooltip2.js';
import { d as defineCustomElement$2 } from './wpp-spinner2.js';
import { d as defineCustomElement$1 } from './wpp-tooltip2.js';

const convertMBToBytes = (size) => size * 1024 ** 2;
const getExtension = (filename = '') => `.${filename.split('.').pop()}`;
const getExtensionsList = (acceptConfig) => Object.entries(acceptConfig).reduce((acc, [_, extensions]) => [...acc, ...extensions], []);
const getBaseName = (fileName = '') => fileName.split('.')[0];
const renameFile = (file, newFileName) => new File([file], newFileName, { type: file.type });
const modifyPropertiesOnFile = (file, properties) => new File([file], file.name, { ...properties });

const EXTENSION_TO_TYPE = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  csv: 'text/csv',
  html: 'text/html',
  xml: 'application/xml',
  json: 'application/json',
  png: 'image/png',
  jpg: 'image/jpg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  bmp: 'image/bmp',
  tiff: 'image/tiff',
  svg: 'image/svg+xml',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  mp4: 'video/mp4',
  avi: 'video/x-msvideo',
  mov: 'video/quicktime',
  wmv: 'video/x-ms-wmv',
  flv: 'video/x-flv',
  rar: 'application/x-rar-compressed',
  zip: 'application/zip',
  '7z': 'application/x-7z-compressed',
  tar: 'application/x-tar',
  gz: 'application/gzip',
  bz2: 'application/x-bzip2',
  exe: 'application/x-msdownload',
  dmg: 'application/x-apple-diskimage',
  apk: 'application/vnd.android.package-archive',
  iso: 'application/x-iso9660-image',
  css: 'text/css',
  js: 'application/javascript',
  woff: 'application/font-woff',
  woff2: 'application/font-woff2',
  ttf: 'application/font-sfnt',
  otf: 'application/font-sfnt',
  eot: 'application/vnd.ms-fontobject',
  rtf: 'application/rtf',
  ics: 'text/calendar',
  jsonld: 'application/ld+json',
  key: 'application/x-iwork-keynote-sffkey',
};
const returnIconFromExtension = (fileExtension) => {
  switch (fileExtension) {
    // Text
    case '.txt':
      return h("wpp-icon-document-v2-13-3", null);
    // Compressed
    case '.zip':
    case '.rar':
    case '.7z':
      return h("wpp-icon-file-zip-v2-13-3", null);
    // Image
    case '.png':
    case '.jpg':
    case '.jpeg':
    case '.svg':
    case '.gif':
      return h("wpp-icon-image-v2-13-3", null);
    // Video
    case '.mp4':
    case '.mov':
    case '.avi':
    case '.wmv':
    case '.mkv':
    case '.flv':
    case '.webm':
      return h("wpp-icon-video-clip-v2-13-3", null);
    // Audio
    case '.mp3':
    case '.wav':
    case '.ogg':
    case '.wma':
    case '.m4a':
    case '.aac':
      return h("wpp-icon-music-v2-13-3", null);
    // Data
    case '.csv':
    case '.json':
    case '.xml':
    case '.db':
    case '.sqlite':
    case '.dat':
      return h("wpp-icon-database-v2-13-3", null);
    // Presentation
    case '.pptx':
    case '.key':
    case '.odp':
    case '.pdf':
    case '.pps':
    case '.sldx':
    case '.ppt':
      return h("wpp-icon-pitch-v2-13-3", null);
    // Spreadsheet
    case '.xlsx':
    case '.xls':
    case '.ods':
    case '.numbers':
    case '.tsv':
      return h("wpp-icon-spreadsheet-v2-13-3", null);
    default:
      return h("wpp-icon-file-v2-13-3", null);
  }
};

var sizeFormat;
(function (sizeFormat) {
  sizeFormat["Bytes"] = "Bytes";
  sizeFormat["KB"] = "KB";
  sizeFormat["MB"] = "MB";
  sizeFormat["GB"] = "GB";
})(sizeFormat || (sizeFormat = {}));
var maxSize;
(function (maxSize) {
  maxSize[maxSize["Bytes"] = 10000] = "Bytes";
  maxSize[maxSize["KB"] = 1000000] = "KB";
  maxSize[maxSize["MB"] = 1000000000] = "MB";
})(maxSize || (maxSize = {}));

const wppFileUploadItemCss = ".typography-5xl-display{font-size:var(--wpp-typography-5xl-display-font-size, 48px);font-family:var(--wpp-typography-5xl-display-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-5xl-display-line-height, 62px);letter-spacing:var(--wpp-typography-5xl-display-letter-spacing, 0);text-transform:var(--wpp-typography-5xl-display-text-transform, initial);color:var(--wpp-typography-5xl-display-color, var(--wpp-text-color));font-weight:var(--wpp-typography-5xl-display-font-weight, 600);font-style:var(--wpp-typography-5xl-display-font-style, normal)}.typography-4xl-display{font-size:var(--wpp-typography-4xl-display-font-size, 36px);font-family:var(--wpp-typography-4xl-display-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-4xl-display-line-height, 48px);letter-spacing:var(--wpp-typography-4xl-display-letter-spacing, 0);text-transform:var(--wpp-typography-4xl-display-text-transform, initial);color:var(--wpp-typography-4xl-display-color, var(--wpp-text-color));font-weight:var(--wpp-typography-4xl-display-font-weight, 600);font-style:var(--wpp-typography-4xl-display-font-style, normal)}.typography-3xl-heading{font-size:var(--wpp-typography-3xl-heading-font-size, 28px);font-family:var(--wpp-typography-3xl-heading-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-3xl-heading-line-height, 40px);letter-spacing:var(--wpp-typography-3xl-heading-letter-spacing, 0);text-transform:var(--wpp-typography-3xl-heading-text-transform, initial);color:var(--wpp-typography-3xl-heading-color, var(--wpp-text-color));font-weight:var(--wpp-typography-3xl-heading-font-weight, 600);font-style:var(--wpp-typography-3xl-heading-font-style, normal)}.typography-2xl-heading{font-size:var(--wpp-typography-2xl-heading-font-size, 24px);font-family:var(--wpp-typography-2xl-heading-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-2xl-heading-line-height, 32px);letter-spacing:var(--wpp-typography-2xl-heading-letter-spacing, 0);text-transform:var(--wpp-typography-2xl-heading-text-transform, initial);color:var(--wpp-typography-2xl-heading-color, var(--wpp-text-color));font-weight:var(--wpp-typography-2xl-heading-font-weight, 600);font-style:var(--wpp-typography-2xl-heading-font-style, normal)}.typography-xl-heading{font-size:var(--wpp-typography-xl-heading-font-size, 20px);font-family:var(--wpp-typography-xl-heading-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-xl-heading-line-height, 32px);letter-spacing:var(--wpp-typography-xl-heading-letter-spacing, 0);text-transform:var(--wpp-typography-xl-heading-text-transform, initial);color:var(--wpp-typography-xl-heading-color, var(--wpp-text-color));font-weight:var(--wpp-typography-xl-heading-font-weight, 600);font-style:var(--wpp-typography-xl-heading-font-style, normal)}.typography-l-strong{font-size:var(--wpp-typography-l-body-font-size, 18px);font-family:var(--wpp-typography-l-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-l-body-line-height, 28px);letter-spacing:var(--wpp-typography-l-body-letter-spacing, 0);text-transform:var(--wpp-typography-l-body-text-transform, initial);color:var(--wpp-typography-l-strong-color, var(--wpp-text-color));font-weight:var(--wpp-typography-l-strong-font-weight, 600);font-style:var(--wpp-typography-l-strong-font-style, normal)}.typography-l-midi{font-size:var(--wpp-typography-l-body-font-size, 18px);font-family:var(--wpp-typography-l-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-l-body-line-height, 28px);letter-spacing:var(--wpp-typography-l-body-letter-spacing, 0);text-transform:var(--wpp-typography-l-body-text-transform, initial);color:var(--wpp-typography-l-midi-color, var(--wpp-text-color));font-weight:var(--wpp-typography-l-midi-font-weight, 500);font-style:var(--wpp-typography-l-midi-font-style, normal)}.typography-l-body{font-size:var(--wpp-typography-l-body-font-size, 18px);font-family:var(--wpp-typography-l-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-l-body-line-height, 28px);letter-spacing:var(--wpp-typography-l-body-letter-spacing, 0);text-transform:var(--wpp-typography-l-body-text-transform, initial);color:var(--wpp-typography-l-body-color, var(--wpp-text-color));font-weight:var(--wpp-typography-l-body-font-weight, 400);font-style:var(--wpp-typography-l-body-font-style, normal)}.typography-m-strong{font-size:var(--wpp-typography-m-body-font-size, 16px);font-family:var(--wpp-typography-m-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-m-body-line-height, 24px);letter-spacing:var(--wpp-typography-m-body-letter-spacing, 0);text-transform:var(--wpp-typography-m-body-text-transform, initial);color:var(--wpp-typography-m-strong-color, var(--wpp-text-color));font-weight:var(--wpp-typography-m-strong-font-weight, 600);font-style:var(--wpp-typography-m-strong-font-style, normal)}.typography-m-midi{font-size:var(--wpp-typography-m-body-font-size, 16px);font-family:var(--wpp-typography-m-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-m-body-line-height, 24px);letter-spacing:var(--wpp-typography-m-body-letter-spacing, 0);text-transform:var(--wpp-typography-m-body-text-transform, initial);color:var(--wpp-typography-m-midi-color, var(--wpp-text-color));font-weight:var(--wpp-typography-m-midi-font-weight, 500);font-style:var(--wpp-typography-m-midi-font-style, normal)}.typography-m-body{font-size:var(--wpp-typography-m-body-font-size, 16px);font-family:var(--wpp-typography-m-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-m-body-line-height, 24px);letter-spacing:var(--wpp-typography-m-body-letter-spacing, 0);text-transform:var(--wpp-typography-m-body-text-transform, initial);color:var(--wpp-typography-m-body-color, var(--wpp-text-color));font-weight:var(--wpp-typography-m-body-font-weight, 400);font-style:var(--wpp-typography-m-body-font-style, normal)}.typography-s-strong{font-size:var(--wpp-typography-s-body-font-size, 14px);font-family:var(--wpp-typography-s-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-s-body-line-height, 22px);letter-spacing:var(--wpp-typography-s-body-letter-spacing, 0);text-transform:var(--wpp-typography-s-body-text-transform, initial);color:var(--wpp-typography-s-strong-color, var(--wpp-text-color));font-weight:var(--wpp-typography-s-strong-font-weight, 600);font-style:var(--wpp-typography-s-strong-font-style, normal)}.typography-s-midi{font-size:var(--wpp-typography-s-body-font-size, 14px);font-family:var(--wpp-typography-s-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-s-body-line-height, 22px);letter-spacing:var(--wpp-typography-s-body-letter-spacing, 0);text-transform:var(--wpp-typography-s-body-text-transform, initial);color:var(--wpp-typography-s-midi-color, var(--wpp-text-color));font-weight:var(--wpp-typography-s-midi-font-weight, 500);font-style:var(--wpp-typography-s-midi-font-style, normal)}.typography-s-body{font-size:var(--wpp-typography-s-body-font-size, 14px);font-family:var(--wpp-typography-s-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-s-body-line-height, 22px);letter-spacing:var(--wpp-typography-s-body-letter-spacing, 0);text-transform:var(--wpp-typography-s-body-text-transform, initial);color:var(--wpp-typography-s-body-color, var(--wpp-text-color));font-weight:var(--wpp-typography-s-body-font-weight, 400);font-style:var(--wpp-typography-s-body-font-style, normal)}.typography-xs-strong{font-size:var(--wpp-typography-xs-body-font-size, 12px);font-family:var(--wpp-typography-xs-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-xs-body-line-height, 20px);letter-spacing:var(--wpp-typography-xs-body-letter-spacing, 0);text-transform:var(--wpp-typography-xs-body-text-transform, initial);color:var(--wpp-typography-xs-strong-color, var(--wpp-text-color));font-weight:var(--wpp-typography-xs-strong-font-weight, 600);font-style:var(--wpp-typography-xs-strong-font-style, normal)}.typography-xs-midi{font-size:var(--wpp-typography-xs-body-font-size, 12px);font-family:var(--wpp-typography-xs-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-xs-body-line-height, 20px);letter-spacing:var(--wpp-typography-xs-body-letter-spacing, 0);text-transform:var(--wpp-typography-xs-body-text-transform, initial);color:var(--wpp-typography-xs-midi-color, var(--wpp-text-color));font-weight:var(--wpp-typography-xs-midi-font-weight, 500);font-style:var(--wpp-typography-xs-midi-font-style, normal)}.typography-xs-body{font-size:var(--wpp-typography-xs-body-font-size, 12px);font-family:var(--wpp-typography-xs-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-xs-body-line-height, 20px);letter-spacing:var(--wpp-typography-xs-body-letter-spacing, 0);text-transform:var(--wpp-typography-xs-body-text-transform, initial);color:var(--wpp-typography-xs-body-color, var(--wpp-text-color));font-weight:var(--wpp-typography-xs-body-font-weight, 400);font-style:var(--wpp-typography-xs-body-font-style, normal)}.typography-2xs-strong{font-size:var(--wpp-typography-2xs-strong-font-size, 10px);font-family:var(--wpp-typography-2xs-strong-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-2xs-strong-line-height, 20px);letter-spacing:var(--wpp-typography-2xs-strong-letter-spacing, 0.5px);text-transform:var(--wpp-typography-2xs-strong-text-transform, uppercase);color:var(--wpp-typography-2xs-strong-color, var(--wpp-text-color));font-weight:var(--wpp-typography-2xs-strong-font-weight, 700);font-style:var(--wpp-typography-2xs-strong-font-style, normal)}:host{--fu-item-bg-color:var(--wpp-file-upload-item-bg-color, var(--wpp-grey-color-200));--fu-item-height:var(--wpp-file-upload-item-height, 32px);--fu-item-padding:var(--wpp-file-upload-item-padding, 5px 28px 5px 8px);--fu-item-margin:var(--wpp-file-upload-item-margin, 8px 0 0 0);--fu-item-percentage-margin:var(--wpp-file-upload-item-percentage-margin, 0 8px 0 0);--fu-item-name-margin:var(--wpp-file-upload-item-name-margin, 0 8px 0 8px);--fu-item-name-error-margin:var(--wpp-file-upload-item-name-error-margin, 0 8px 0 24px);--fu-item-border-radius:var(--wpp-file-upload-item-border-radius, var(--wpp-border-radius-xs));--fu-item-item-color:var(--wpp-file-upload-item-color, var(--wpp-grey-color-700));--fu-item-item-name-color:var(--wpp-file-upload-item-name-color, var(--wpp-grey-color-900));--fu-item-close-icon-color-hover:var(--wpp-file-upload-item-close-icon-color-hover, var(--wpp-icon-color-hover));--fu-item-close-icon-active-color:var(--wpp-file-upload-item-close-icon-active-color, var(--wpp-icon-color-active));--fu-item-close-icon-first-border-color-focus:var(--wpp-file-upload-item-close-icon-first-border-color-focus, var(--wpp-grey-color-000));--fu-item-close-icon-second-border-color-focus:var(--wpp-file-upload-item-close-icon-second-border-color-focus, var(--wpp-brand-color));--fu-item-loading-margin:var(--wpp-file-upload-item-loading-margin, 0px 20px 0px 0px);--fu-item-error-color:var(--wpp-file-upload-item-error-color, var(--wpp-text-color-danger));--fu-item-bg-color-disabled:var(--wpp-file-upload-item-bg-color-disabled, var(--wpp-grey-color-200));--fu-item-text-color-disabled:var(--wpp-file-upload-item-text-color-disabled, var(--wpp-text-color-disabled))}.item-wrapper{position:relative;display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center;-ms-flex-pack:justify;justify-content:space-between;-webkit-box-sizing:border-box;box-sizing:border-box;-ms-flex-wrap:wrap;flex-wrap:wrap;max-width:var(--fu-item-max-width);min-height:var(--fu-item-height);background-color:var(--fu-item-bg-color);padding:var(--fu-item-padding);border-radius:var(--fu-item-border-radius);margin:var(--fu-item-margin);font-size:var(--wpp-typography-s-body-font-size, 14px);font-family:var(--wpp-typography-s-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-s-body-line-height, 22px);letter-spacing:var(--wpp-typography-s-body-letter-spacing, 0);text-transform:var(--wpp-typography-s-body-text-transform, initial);color:var(--wpp-typography-s-body-color, var(--wpp-text-color));font-weight:var(--wpp-typography-s-body-font-weight, 400);font-style:var(--wpp-typography-s-body-font-style, normal)}.item-wrapper.disabled{background-color:var(--fu-item-bg-color-disabled);color:var(--fu-item-text-color-disabled);cursor:not-allowed}.item-wrapper.disabled .name,.item-wrapper.disabled .loading{color:var(--fu-item-text-color-disabled)}.item-wrapper.disabled .wpp-icon{color:var(--wpp-icon-color-disabled);pointer-events:none}.block{display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center}.percentage{color:var(--fu-item-item-color);margin:var(--fu-item-percentage-margin)}.loading{color:var(--fu-item-item-color);margin:var(--fu-item-loading-margin)}.name{margin:var(--fu-item-name-margin);color:var(--fu-item-item-name-color)}.name.name-error{margin:var(--fu-item-name-error-margin)}.error{-ms-flex:1;flex:1;margin:0}.content-wrapper{width:100%;display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center;-ms-flex-pack:justify;justify-content:space-between}.cross-icon{position:absolute;right:5px;top:6px;cursor:pointer;-webkit-transition:0.2s ease-in-out;transition:0.2s ease-in-out}.cross-icon:hover{color:var(--fu-item-close-icon-color-hover)}.cross-icon:active{color:var(--fu-item-close-icon-active-color)}.inline-message-error::part(message-block){font-size:var(--wpp-typography-s-body-font-size, 14px);font-family:var(--wpp-typography-s-body-font-family, var(--wpp-font-family));line-height:var(--wpp-typography-s-body-line-height, 22px);letter-spacing:var(--wpp-typography-s-body-letter-spacing, 0);text-transform:var(--wpp-typography-s-body-text-transform, initial);color:var(--wpp-typography-s-strong-color, var(--wpp-text-color));font-weight:var(--wpp-typography-s-strong-font-weight, 600);font-style:var(--wpp-typography-s-strong-font-style, normal);color:var(--fu-item-error-color)}:host(:focus-visible){outline:none}:host(:focus-visible) .cross-icon{border-radius:3px;outline:none;-webkit-box-shadow:0 0 0 1px var(--fu-item-close-icon-first-border-color-focus), 0 0 0 3px var(--fu-item-close-icon-second-border-color-focus);box-shadow:0 0 0 1px var(--fu-item-close-icon-first-border-color-focus), 0 0 0 3px var(--fu-item-close-icon-second-border-color-focus)}";

const WppFileUploadItem = /*@__PURE__*/ proxyCustomElement(class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.__attachShadow();
    this.wppDelete = createEvent(this, "wppDelete", 1);
    this.wppClick = createEvent(this, "wppClick", 1);
    this.convertToAppropriateFormat = (size) => {
      if (size < maxSize.Bytes) {
        this.measurementUnit = sizeFormat.Bytes;
        return size.toFixed(1);
      }
      if (size < maxSize.KB) {
        this.measurementUnit = sizeFormat.KB;
        return Math.floor(size / 1000).toFixed(1);
      }
      if (size < maxSize.MB) {
        this.measurementUnit = sizeFormat.MB;
        return (size / 1000000).toFixed(1);
      }
      this.measurementUnit = sizeFormat.GB;
      return (size / 1000000000).toFixed(1);
    };
    this.setReaderFormat = (reader) => {
      switch (this.format) {
        case 'arrayBuffer': {
          return reader.readAsArrayBuffer(this.file);
        }
        case 'binaryString': {
          return reader.readAsBinaryString(this.file);
        }
        default: {
          return reader.readAsDataURL(this.file);
        }
      }
    };
    this.isFileLoading = () => this.file.isLoading || !this.isLoadingFinished;
    this.setCurrentIcon = () => {
      if (this.isFileLoading())
        return h("wpp-spinner-v2-13-3", null);
      const { name } = this.file;
      if (this.isFileWithError())
        return null;
      const fileExtension = getExtension(name);
      return returnIconFromExtension(fileExtension);
    };
    this.getErrorMessage = () => {
      if (this.file.sizeError)
        return this.locales.sizeError;
      if (this.file.formatError)
        return this.locales.formatError;
      return this.file.validatorError || '';
    };
    this.isFileWithError = () => !!(this.file.sizeError || this.file.formatError || this.file.validatorError);
    this.setCurrentError = () => {
      if (this.isFileWithError()) {
        const currentError = this.getErrorMessage();
        return (h("wpp-inline-message-v2-13-3", { class: "inline-message-error", message: currentError, type: "error", showTooltipFrom: 140, tooltipConfig: { popperOptions: { strategy: 'fixed' } } }));
      }
      return null;
    };
    this.getEventData = () => ({
      index: this.currentIndex,
      name: this.file.name,
      size: this.file.size,
    });
    this.handleCloseClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (this.file.disabled)
        return;
      this.wppDelete.emit(this.getEventData());
    };
    this.handleClick = () => {
      if (this.file.disabled)
        return;
      this.wppClick.emit(this.getEventData());
    };
    this.blockCssClasses = () => ({
      block: true,
    });
    this.fileNameCssClasses = () => ({
      name: true,
      'name-error': this.isFileWithError(),
    });
    this.hostCssClasses = () => ({
      'wpp-file-upload-item': true,
      'file-upload-item': true,
    });
    this.itemCssClasses = () => ({
      'item-wrapper': true,
      disabled: !!this.file.disabled,
    });
    this.percentage = 0;
    this.total = 0;
    this.loaded = 0;
    this.isLoadingFinished = false;
    this.measurementUnit = sizeFormat.MB;
    this.file = undefined;
    this.format = 'base64';
    this.maxLabelLength = 30;
    this.currentIndex = undefined;
    this.locales = undefined;
  }
  componentWillLoad() {
    const { size } = this.file;
    if (this.isFileWithError()) {
      this.isLoadingFinished = true;
      this.total = +this.convertToAppropriateFormat(size);
      return;
    }
    if ('url' in this.file) {
      this.isLoadingFinished = true;
      this.total = +this.convertToAppropriateFormat(size);
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const currentTarget = event.currentTarget;
      this.file.result = currentTarget.result;
    };
    reader.onprogress = (event) => {
      this.loaded = +this.convertToAppropriateFormat(event.loaded);
      this.total = +this.convertToAppropriateFormat(event.total);
      const currentPercent = (event.loaded / event.total) * 100;
      this.percentage = +currentPercent.toFixed(1);
    };
    reader.onloadend = () => {
      this.isLoadingFinished = true;
    };
    this.setReaderFormat(reader);
  }
  render() {
    return (h(Host, { class: this.hostCssClasses(), exportparts: "file-item, wrapper, content, file-name, tooltip, loading, percentage, cross-icon", onClick: this.handleClick }, h("li", { class: this.itemCssClasses(), part: "file-item" }, this.setCurrentError(), h("div", { class: "content-wrapper", part: "wrapper" }, h("div", { class: this.blockCssClasses(), part: "content" }, this.setCurrentIcon(), h("p", { class: this.fileNameCssClasses(), part: "file-name" }, this.file.name.length > this.maxLabelLength ? (h("wpp-tooltip-v2-13-3", { text: this.file.name, config: { popperOptions: { strategy: 'fixed' } }, part: "tooltip" }, truncate(this.file.name, this.maxLabelLength, true))) : (this.file.name)), this.isFileWithError() ? null : (h("p", { class: "loading", part: "loading" }, this.isFileLoading()
      ? `${this.loaded}/${this.total} ${this.measurementUnit}`
      : `${this.total} ${this.measurementUnit}`))), !this.isFileLoading() ? null : (h("p", { class: "percentage", part: "percentage" }, this.percentage, "%")), this.file.deletable !== false && (h("wpp-icon-cross-v2-13-3", { class: "cross-icon", part: "cross-icon", onClick: this.handleCloseClick }))))));
  }
  static get registryIs() { return "wpp-file-upload-item-v2-13-3"; }
  static get style() { return wppFileUploadItemCss; }
}, [1, "wpp-file-upload-item", "wpp-file-upload-item-v2-13-3", {
    "file": [1040],
    "format": [1],
    "maxLabelLength": [2, "max-label-length"],
    "currentIndex": [2, "current-index"],
    "locales": [16],
    "percentage": [32],
    "total": [32],
    "loaded": [32],
    "isLoadingFinished": [32],
    "measurementUnit": [32]
  }]);
function defineCustomElement() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["wpp-file-upload-item-v2-13-3", "wpp-action-button-v2-13-3", "wpp-icon-cross-v2-13-3", "wpp-icon-database-v2-13-3", "wpp-icon-document-v2-13-3", "wpp-icon-error-v2-13-3", "wpp-icon-file-v2-13-3", "wpp-icon-file-zip-v2-13-3", "wpp-icon-image-v2-13-3", "wpp-icon-info-v2-13-3", "wpp-icon-music-v2-13-3", "wpp-icon-pitch-v2-13-3", "wpp-icon-spreadsheet-v2-13-3", "wpp-icon-success-v2-13-3", "wpp-icon-video-clip-v2-13-3", "wpp-icon-warning-v2-13-3", "wpp-inline-message-v2-13-3", "wpp-internal-tooltip-v2-13-3", "wpp-spinner-v2-13-3", "wpp-tooltip-v2-13-3"];
  components.forEach(tagName => { switch (tagName) {
    case "wpp-file-upload-item-v2-13-3":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, WppFileUploadItem);
      }
      break;
    case "wpp-action-button-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$j();
      }
      break;
    case "wpp-icon-cross-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$i();
      }
      break;
    case "wpp-icon-database-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$h();
      }
      break;
    case "wpp-icon-document-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$g();
      }
      break;
    case "wpp-icon-error-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$f();
      }
      break;
    case "wpp-icon-file-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$e();
      }
      break;
    case "wpp-icon-file-zip-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$d();
      }
      break;
    case "wpp-icon-image-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$c();
      }
      break;
    case "wpp-icon-info-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$b();
      }
      break;
    case "wpp-icon-music-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$a();
      }
      break;
    case "wpp-icon-pitch-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$9();
      }
      break;
    case "wpp-icon-spreadsheet-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$8();
      }
      break;
    case "wpp-icon-success-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$7();
      }
      break;
    case "wpp-icon-video-clip-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$6();
      }
      break;
    case "wpp-icon-warning-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$5();
      }
      break;
    case "wpp-inline-message-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$4();
      }
      break;
    case "wpp-internal-tooltip-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$3();
      }
      break;
    case "wpp-spinner-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
    case "wpp-tooltip-v2-13-3":
      if (!customElements.get(tagName)) {
        defineCustomElement$1();
      }
      break;
  } });
}
defineCustomElement();

export { EXTENSION_TO_TYPE as E, WppFileUploadItem as W, getBaseName as a, getExtensionsList as b, convertMBToBytes as c, defineCustomElement as d, getExtension as g, modifyPropertiesOnFile as m, renameFile as r };
